import {
    Serializer as JSONAPISerializer,
    Deserializer as JSONAPIDeserializer,
} from 'jsonapi-serializer';

/**
 * Конфигурация связи (relationship) в JSON:API (может быть вложенной).
 */
interface JSONAPIRelationshipConfig {
    /** Поле, выступающее идентификатором связанного ресурса (по умолчанию `id`) */
    ref?: string;
    /** Простейшие атрибуты, которые надо передавать вместе с ресурсом */
    attributes?: string[];
    /** Включать ли ресурс в секцию `included` */
    included?: boolean;
    /** Вложенные связи */
    relationships?: Record<string, JSONAPIRelationshipConfig>;
}

/**
 * Схема ресурса для (де)сериализации.
 */
interface JSONAPISchema {
    /** Атрибуты верхнего уровня */
    attributes: string[];
    /** Описания связей (key → config) */
    relationships?: Record<string, JSONAPIRelationshipConfig>;
}

/**
 * Настройки преобразования регистра полей.
 */
interface JSONAPIOptions {
    /**
     * Кейс полей при ОТПРАВКЕ на бэк (camelCase → ?)
     *   * `'underscore_case'` — snake_case (Rails / Phoenix)
     *   * `'dash-case'`        — kebab‑case
     *   * `'camelCase'`        — оставить как есть
     *   * `(attr) => string`   — своя функция
     *   *По умолчанию*: `'underscore_case'`.
     */
    backendCase?: 'underscore_case' | 'dash-case' | 'camelCase' | ((attr: string) => string);
    /**
     * Кейс полей при ПРИЁМЕ с бэка (→ camelCase).
     * Те же варианты. *По умолчанию*: `'camelCase'`.
     */
    frontCase?: 'underscore_case' | 'dash-case' | 'camelCase' | ((attr: string) => string);
}

/**
 * Формирует экземпляр `jsonapi-serializer` по заданной схеме.
 * Вызывается внутри `serialize()`.
 */
function buildSerializer<T = any>(
    type: string,
    schema: JSONAPISchema,
    options: JSONAPIOptions = {},
): JSONAPISerializer<T> {
    const { attributes, relationships } = schema;
    const { backendCase = 'underscore_case' } = options;

    const serializerOptions: any = {
        keyForAttribute: backendCase,
        attributes,
    };

    if (relationships) {
        Object.entries(relationships).forEach(([key, cfg]) => {
            serializerOptions[key] = mapRelationshipConfig(cfg);
        });
    }

    // @ts-ignore — типы пакета не детализированы, на рантайме всё работает
    return new JSONAPISerializer(type, serializerOptions);
}

function mapRelationshipConfig(cfg: JSONAPIRelationshipConfig): any {
    const opts: any = {
        ref: cfg.ref ?? 'id',
        included: cfg.included ?? false,
        attributes: cfg.attributes ?? [],
    };
    if (cfg.relationships) {
        Object.entries(cfg.relationships).forEach(([nestedKey, nestedCfg]) => {
            opts[nestedKey] = mapRelationshipConfig(nestedCfg);
        });
    }
    return opts;
}

/**
 * ➡️  Сериализация: JS‑объект(ы) → JSON:API payload.
 */
function serialize<T = any>(
    type: string,
    data: T | T[],
    schema: JSONAPISchema,
    options?: JSONAPIOptions,
) {
    const serializer = buildSerializer<T>(type, schema, options);
    return serializer.serialize(data);
}

/**
 * ⬅️  Десериализация: JSON:API payload → «плоский» объект JS.
 */
async function deserialize<T = any>(
    payload: any,
    options: JSONAPIOptions = {},
): Promise<T | T[]> {
    const { frontCase = 'camelCase' } = options;
    const deserializer = new JSONAPIDeserializer({
        keyForAttribute: frontCase,
        pluralizeType: false,
    });

    return deserializer.deserialize(payload) as unknown as T | T[];
}

/**
 * Генератор схемы по образцу объекта.
 */
function inferSchemaFromSample(sample: Record<string, any>): JSONAPISchema {
    const attributes: string[] = [];
    const relationships: Record<string, JSONAPIRelationshipConfig> = {};

    Object.entries(sample).forEach(([key, value]) => {
        if (key === 'id') return;
        if (isRelationship(value)) {
            relationships[key] = { ref: 'id', included: true };
        } else {
            attributes.push(key);
        }
    });
    return {
        attributes,
        relationships: Object.keys(relationships).length ? relationships : undefined,
    };
}

function isRelationship(value: any): boolean {
    if (Array.isArray(value)) {
        return !!value.length && typeof value[0] === 'object' && 'id' in value[0];
    }
    return typeof value === 'object' && value !== null && 'id' in value;
}

// ---------------------------------------------------------------------------
// Экспортируем ОДИН‑ЕДИНСТВЕННЫЙ объект JSONAPI с нужными методами
// ---------------------------------------------------------------------------
const JSONAPI = {
    serialize,
    deserialize,
    inferSchemaFromSample,
};

export default JSONAPI;
